/**
 * 📊 PublicImpact — Wave 1 · Public Impact Bridge (Iter279)
 * ════════════════════════════════════════════════════════════════════════
 *
 * The dedicated donor / grantmaker / corporate-partner surface. Pulls
 * live aggregates from /api/v1/public/community-impact and animates
 * each counter from 0 → its live value when the section scrolls into
 * view. All numbers are computed server-side from the real operational
 * ledger — no hardcoded totals, no PII.
 *
 * Sections:
 *   1. Hero band — big total cost-avoidance figure + live-data badge
 *   2. Cost-avoidance breakdown grid (incarceration / ER / shelter)
 *   3. Residents housed vs capacity (progress bar)
 *   4. Workforce pipeline counter
 *   5. Program hours + velocity strip
 *   6. Window toggle (All-Time since May 1 · Last 30 Days)
 *   7. Powered-by footer
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Zap, Users, Briefcase, ShieldCheck, TrendingUp, Home, ExternalLink, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ImpactThermometer from "../components/ImpactThermometer";
import { DonorTiersStrip } from "./Donate";
import useDocumentMeta from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;


// ────────────────────────────────────────────────────────────────────
// Counter hook — eases 0 → target when the element enters the viewport.
// ────────────────────────────────────────────────────────────────────
function useTickerOnScroll(target, opts = {}) {
  const { duration = 1600, decimals = 0 } = opts;
  const [value, setValue] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.35 },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    if (!Number.isFinite(target) || target <= 0) { setValue(0); return undefined; }
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(step);
      else setValue(target);
    };
    raf = requestAnimationFrame(step);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [visible, target, duration]);

  const rendered = decimals ? value.toFixed(decimals) : Math.round(value).toLocaleString();
  return { ref, rendered };
}

const fmtUSD = (n) => "$" + Math.round(n || 0).toLocaleString();
const fmtUSDShort = (n) => {
  const v = Math.round(n || 0);
  if (v >= 1000) return "$" + (v / 1000).toFixed(v >= 10000 ? 0 : 1) + "k";
  return "$" + v;
};


// ────────────────────────────────────────────────────────────────────
// Individual card components — each owns its own ticker so the eye
// tracks the numbers separately (no synchronised group animation).
// ────────────────────────────────────────────────────────────────────
function DollarTicker({ target, prefix = "$", suffix = "+" }) {
  const { ref, rendered } = useTickerOnScroll(target);
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{rendered}{suffix}
    </span>
  );
}

function IntTicker({ target, suffix = "" }) {
  const { ref, rendered } = useTickerOnScroll(target);
  return <span ref={ref} className="tabular-nums">{rendered}{suffix}</span>;
}


// ────────────────────────────────────────────────────────────────────
export default function PublicImpact() {
  useDocumentMeta({
    title: "Community Impact & Savings | The Exchange Alliance",
    description: "Live, verified metrics from The Exchange Alliance operations — cost avoidance, workforce placement, and program velocity since May 1, 2026.",
    canonical: "https://theexchangealliance.com/impact",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [snap, setSnap] = useState(null);
  const [timeWindow, setTimeWindow] = useState("all");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/v1/public/community-impact?window=${timeWindow}`)
      .then((r) => { if (!cancelled) setSnap(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Failed to load."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [timeWindow]);

  const cost = snap?.cost_avoidance || {};
  const beds = snap?.residents_housed || {};
  const wp   = snap?.workforce_pipeline || {};
  const hrs  = snap?.program_hours || {};
  const lab  = snap?.labor || {};
  const vel  = snap?.velocity || {};

  return (
    <main data-testid="public-impact-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <Header />

      {/* ───── Hero band ───── */}
      <section
        className="pt-28 pb-16 md:pt-36 md:pb-24 border-b border-[#0A0612]/5 relative overflow-hidden"
        data-testid="impact-hero"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-50 text-emerald-900 font-mono text-[10px] tracking-[0.22em] uppercase mb-6"
              data-testid="live-data-badge"
              title="Powered by The Exchange Alliance Management System — updates every 5 minutes"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <Zap className="h-3 w-3" />
              Live Data · Powered by The Exchange Alliance Management System
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
              Every number below is <span className="text-[#1B4332]">real</span>, pulled straight from our operations.
            </h1>
            <p className="text-lg md:text-xl text-[#0A0612]/70 max-w-2xl leading-relaxed mb-10">
              Since <strong>May 1, 2026</strong>, The Exchange Alliance has been housing residents,
              coordinating touchpoints, and dispatching labor across a live operations
              network. Here&apos;s what that has produced.
            </p>

            {/* Window toggle */}
            <div className="inline-flex bg-white border border-[#0A0612]/10 rounded-full p-1 shadow-sm" role="tablist" data-testid="impact-window-toggle">
              <button
                type="button"
                onClick={() => setTimeWindow("all")}
                data-testid="impact-window-all"
                aria-selected={timeWindow === "all"}
                className={`px-4 py-1.5 rounded-full font-mono text-[11px] tracking-[0.18em] uppercase transition-all ${
                  timeWindow === "all" ? "bg-[#0A0612] text-[#F4D88A]" : "text-[#0A0612]/70 hover:text-[#0A0612]"
                }`}
              >
                All-Time (Since May 1, 2026)
              </button>
              <button
                type="button"
                onClick={() => setTimeWindow("30d")}
                data-testid="impact-window-30d"
                aria-selected={timeWindow === "30d"}
                className={`px-4 py-1.5 rounded-full font-mono text-[11px] tracking-[0.18em] uppercase transition-all ${
                  timeWindow === "30d" ? "bg-[#0A0612] text-[#F4D88A]" : "text-[#0A0612]/70 hover:text-[#0A0612]"
                }`}
              >
                Last 30 Days
              </button>
            </div>
          </div>
        </div>
      </section>

      {loading && (
        <section className="py-16 text-center text-[#0A0612]/60" data-testid="impact-loading">
          Loading live impact snapshot…
        </section>
      )}

      {err && !loading && (
        <section className="py-16 text-center text-red-700" data-testid="impact-error">
          Could not load live data — please refresh the page.
        </section>
      )}

      {snap && !loading && (
        <>
          {/* ───── Big cost-avoidance card ───── */}
          <section className="py-16 md:py-24 border-b border-[#0A0612]/5" data-testid="impact-cost-hero">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
              <div className="rounded-3xl bg-gradient-to-br from-[#0A0612] to-[#1B4332] text-white p-10 md:p-16 shadow-2xl">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4D88A]/80 mb-4">
                  Aggregated Taxpayer & Community Cost Savings
                </div>
                <div className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-none">
                  <DollarTicker target={cost.total || 0} />
                </div>
                <div className="mt-6 text-lg md:text-xl text-white/70 max-w-3xl">
                  Every dollar reflects an outcome that DIDN&apos;T happen because a resident was
                  housed, supported, and moving toward self-sufficiency instead.
                </div>
              </div>
            </div>
          </section>

          {/* ───── 3-card cost breakdown ───── */}
          <section className="py-16 md:py-20 border-b border-[#0A0612]/5" data-testid="impact-cost-breakdown">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] font-bold mb-4">
                Cost Avoidance · Line Items
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-10">
                Where the savings come from
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <CostCard
                  testid="cost-incarceration"
                  icon={<ShieldCheck className="h-5 w-5" />}
                  accent="#7C3AED"
                  label="Avoided Incarceration"
                  value={cost.avoided_incarceration}
                  caption="Days housed × $120 baseline correctional cost."
                />
                <CostCard
                  testid="cost-er"
                  icon={<TrendingUp className="h-5 w-5" />}
                  accent="#2563EB"
                  label="Avoided ER Visits"
                  value={cost.avoided_er_visits}
                  caption="Preventive support × emergency-department average cost."
                />
                <CostCard
                  testid="cost-shelter"
                  icon={<Home className="h-5 w-5" />}
                  accent="#059669"
                  label="Avoided Emergency Shelter Cost"
                  value={cost.avoided_shelter}
                  caption="Days housed × $80 emergency-shelter unit rate."
                />
              </div>
            </div>
          </section>

          {/* ───── Housed + Workforce strip ───── */}
          <section className="py-16 md:py-20 border-b border-[#0A0612]/5 bg-white" data-testid="impact-housed-workforce">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Housed */}
              <div className="rounded-3xl border border-[#0A0612]/10 p-8 md:p-10 bg-[#FAFAF7]" data-testid="impact-housed-card">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-full bg-[#1B4332] text-[#F4D88A] grid place-items-center">
                    <Home className="h-5 w-5" />
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0612]/60">
                    Residents Actively Housed
                  </div>
                </div>
                <div className="font-display text-5xl md:text-6xl font-bold tracking-tight">
                  <IntTicker target={beds.active || 0} /> <span className="text-[#0A0612]/40 text-3xl md:text-4xl font-normal">/ {beds.capacity || 0}</span>
                </div>
                <div className="mt-4 h-2 bg-[#0A0612]/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1B4332] transition-all duration-1000"
                    style={{ width: `${beds.capacity ? Math.min(100, Math.round((beds.active / beds.capacity) * 100)) : 0}%` }}
                    data-testid="impact-housed-progress"
                  />
                </div>
                <div className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#0A0612]/60">
                  {beds.capacity ? Math.round((beds.active / beds.capacity) * 100) : 0}% bed utilisation
                </div>
              </div>

              {/* Workforce pipeline */}
              <div className="rounded-3xl border border-[#0A0612]/10 p-8 md:p-10 bg-[#FAFAF7]" data-testid="impact-workforce-card">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-full bg-[#D4AF37] text-[#0A0612] grid place-items-center">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0612]/60">
                    Workforce Pipeline · Phase I–III
                  </div>
                </div>
                <div className="font-display text-5xl md:text-6xl font-bold tracking-tight">
                  <IntTicker target={wp.active_pipeline || 0} />
                </div>
                <div className="mt-4 text-[#0A0612]/70 leading-relaxed">
                  Residents currently rebuilding — moving through stabilization,
                  vocational prep, and transition into paid placement.
                </div>
              </div>
            </div>
          </section>

          {/* ───── Program hours + velocity ───── */}
          <section className="py-16 md:py-20 border-b border-[#0A0612]/5" data-testid="impact-hours-velocity">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
              <div className="mb-8 max-w-2xl">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#C9A227] mb-2">Real-Time Care Ledger</div>
                <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0A0612]">
                  Every logged note = one hour served.
                </h3>
                <p className="text-sm text-[#0A0612]/60 mt-2 leading-relaxed">
                  Each case management, life-skills, and resident check-up note that a staffer writes in the platform counts as one full hour of served time — no estimates, no rounding.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <HourCard
                  testid="hours-case"
                  icon={<Users className="h-5 w-5" />}
                  accent="#7C3AED"
                  label="Case Management Hours"
                  value={hrs.case_management_hours}
                  caption="Each 1:1 case-manager note logged = one hour served."
                />
                <HourCard
                  testid="hours-life"
                  icon={<Users className="h-5 w-5" />}
                  accent="#059669"
                  label="Life Skills Hours"
                  value={hrs.life_skills_hours}
                  caption="Each life-skills curriculum note logged = one hour served."
                />
                <HourCard
                  testid="hours-checkup"
                  icon={<Users className="h-5 w-5" />}
                  accent="#DC2626"
                  label="Resident Check-Up Hours"
                  value={hrs.residential_checkup_hours}
                  caption="Each resident check-up note logged = one hour served."
                />
                <HourCard
                  testid="hours-labor"
                  icon={<Briefcase className="h-5 w-5" />}
                  accent="#D4AF37"
                  label="Labor Hours Dispatched"
                  value={lab.labor_hours_dispatched}
                  caption={`≈ ${fmtUSD(lab.wages_earned_usd || 0)} in resident wages earned.`}
                />
              </div>

              <div className="mt-10 rounded-2xl border border-[#0A0612]/10 bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="impact-velocity-card">
                <div className="h-14 w-14 rounded-full bg-[#1B4332] text-[#F4D88A] grid place-items-center flex-shrink-0">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0612]/60">
                    Weekly Engagement Velocity
                  </div>
                  <div className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
                    <IntTicker target={vel.green_light_pct || 0} suffix="%" />
                    <span className="text-[#0A0612]/50 text-base font-normal ml-3">
                      of {vel.active_measured || 0} pipeline residents touched by staff in the last 7 days
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ───── Footer stamp ───── */}
          <section className="py-10 bg-white border-b border-[#0A0612]/5">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 text-center">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0A0612]/50 mb-2">
                Snapshot generated {snap.as_of ? new Date(snap.as_of).toLocaleString() : "just now"} · Cached ≤ 5 min
              </div>
              <div className="text-sm text-[#0A0612]/70">
                Zero personally identifiable information is exposed on this page — every field
                is aggregated across the resident population.
              </div>
            </div>
          </section>
        </>
      )}

      {/* 🌡️ Live Impact Thermometer — SA-editable quarterly campaign
          goal + progress bar. Silent-hides when campaign is inactive. */}
      <ImpactThermometer />

      {/* 💝 Wave 5 · Donor tier strip — 3 fixed gift levels routing
          to Stripe Payment Links + confetti on tap. */}
      <DonorTiersStrip />

      <Footer />
    </main>
  );
}


function CostCard({ testid, icon, accent, label, value, caption }) {
  return (
    <div
      className="rounded-2xl border border-[#0A0612]/10 bg-white p-6 md:p-8 hover:shadow-xl transition-all"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
        <span className="font-mono text-[9px] tracking-[0.25em] uppercase font-bold" style={{ color: accent }}>
          {label}
        </span>
      </div>
      <div className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0A0612]">
        <DollarTicker target={value || 0} />
      </div>
      <div className="mt-3 text-sm text-[#0A0612]/60 leading-relaxed">
        {caption}
      </div>
    </div>
  );
}


function HourCard({ testid, icon, accent, label, value, caption }) {
  return (
    <div
      className="rounded-2xl border border-[#0A0612]/10 bg-white p-6 md:p-8 hover:shadow-xl transition-all"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
        <span className="font-mono text-[9px] tracking-[0.25em] uppercase font-bold" style={{ color: accent }}>
          {label}
        </span>
      </div>
      <div className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0A0612]">
        <IntTicker target={value || 0} />
      </div>
      <div className="mt-3 text-sm text-[#0A0612]/60 leading-relaxed">
        {caption}
      </div>
    </div>
  );
}


/**
 * Compact CTA band exported for the homepage to nudge visitors toward
 * the /impact page. Kept in this file so both the card + the destination
 * live together and stay stylistically in sync.
 */
export function ImpactHomepageCTA() {
  return (
    <section
      className="bg-[#0A0612] text-white py-10 md:py-14 border-y border-[#D4AF37]/40"
      data-testid="impact-homepage-cta"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/50 text-emerald-300 bg-emerald-500/10 font-mono text-[10px] tracking-[0.22em] uppercase mb-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Live Data
          </div>
          <div className="font-display text-2xl md:text-3xl font-bold tracking-tight leading-tight">
            Real dollars, real people housed, real hours worked — see the receipts.
          </div>
        </div>
        <a
          href="/impact"
          data-testid="homepage-impact-cta-btn"
          className="inline-flex items-center gap-2 bg-[#F4D88A] text-[#0A0612] hover:bg-[#D4AF37] font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-3.5 rounded-none flex-shrink-0 transition-colors border border-[#D4AF37]/60"
        >
          See Our Live Impact
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}
